import { Badge } from '@oysterjs/ui/badge';
import { Button } from '@oysterjs/ui/button';
import { Spinner } from '@oysterjs/ui/spinner';
import { GetUsageOverageInvoicesQuery, useGetUsageOverageInvoicesQuery } from '~/api/graphql';
import { formatUsd } from './usage-limits-utils';
import { formatUsagePeriodRange } from './usage-utils';
import { overageInvoiceStatusColor, overageInvoiceStatusLabel } from './usage-overage-utils';

const PAGE_SIZE = 10;

type UsageOverageInvoicesProps = {
  timezone: string;
};

export const UsageOverageInvoices = ({ timezone }: UsageOverageInvoicesProps) => {
  const { data, loading, error, fetchMore } = useGetUsageOverageInvoicesQuery({
    variables: { limit: PAGE_SIZE },
    fetchPolicy: 'cache-first',
    notifyOnNetworkStatusChange: true
  });

  const connection = data?.agencyUser?.agency.subscription?.usageOverageInvoices;
  const edges = connection?.edges ?? [];
  const pageInfo = connection?.pageInfo;
  const hasNextPage = pageInfo?.hasNextPage ?? false;
  const endCursor = pageInfo?.endCursor;

  const onLoadMore = () => {
    if (!endCursor || !hasNextPage) {
      return;
    }
    void fetchMore({
      variables: { limit: PAGE_SIZE, cursor: endCursor },
      updateQuery: (previous, { fetchMoreResult }) =>
        mergeUsageOverageInvoicePages(previous, fetchMoreResult)
    });
  };

  return (
    <div className="divide-y divide-neutral-200 dark:divide-neutral-700 overflow-hidden rounded-lg bg-white dark:bg-neutral-800 shadow">
      <div className="px-4 py-5 sm:px-6">
        <h3 className="m-0 p-0 text-lg font-medium text-neutral-900 dark:text-white">
          Overage invoices
        </h3>
      </div>
      <div className="px-4 py-5 sm:px-6">
        {error ? (
          <p className="m-0 py-6 text-center text-sm text-red-600 dark:text-red-400">
            Failed to load overage invoices.
          </p>
        ) : loading && edges.length === 0 ? (
          <div className="flex justify-center py-6">
            <Spinner size="size-6" color="neutral" />
          </div>
        ) : edges.length === 0 ? (
          <p className="m-0 py-6 text-center text-sm text-neutral-500 dark:text-neutral-400">
            No overage invoices yet.
          </p>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-left text-sm">
              <thead>
                <tr className="text-xs font-medium uppercase tracking-wide text-neutral-500 dark:text-neutral-400">
                  <th className="pb-3 pr-4 font-medium">Period</th>
                  <th className="pb-3 pr-4 font-medium">Status</th>
                  <th className="pb-3 text-right font-medium">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-neutral-100 dark:divide-neutral-700">
                {edges.map((edge) => {
                  const invoice = edge.node;
                  const period =
                    formatUsagePeriodRange(invoice.periodStart, invoice.periodEnd, timezone) ?? '—';
                  return (
                    <tr key={invoice.id}>
                      <td className="py-3 pr-4">
                        {invoice.hostedInvoiceUrl ? (
                          <a
                            href={invoice.hostedInvoiceUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="text-neutral-900 dark:text-white underline-offset-2 hover:underline"
                          >
                            {period}
                          </a>
                        ) : (
                          <span className="text-neutral-900 dark:text-white">{period}</span>
                        )}
                      </td>
                      <td className="py-3 pr-4">
                        <Badge color={overageInvoiceStatusColor(invoice.status)}>
                          {overageInvoiceStatusLabel(invoice.status)}
                        </Badge>
                      </td>
                      <td className="py-3 text-right text-neutral-900 dark:text-white">
                        {formatUsd(invoice.totalDollars)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
        {hasNextPage && (
          <div className="mt-4 flex justify-center">
            <Button color="white" type="button" onClick={onLoadMore} disabled={loading}>
              {loading ? 'Loading…' : 'Load more'}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
};

export const mergeUsageOverageInvoicePages = (
  previous: GetUsageOverageInvoicesQuery,
  incoming: GetUsageOverageInvoicesQuery | undefined
): GetUsageOverageInvoicesQuery => {
  const previousConnection = previous.agencyUser?.agency.subscription?.usageOverageInvoices;
  const incomingConnection = incoming?.agencyUser?.agency.subscription?.usageOverageInvoices;
  if (!incoming || !previousConnection || !incomingConnection) {
    return incoming ?? previous;
  }

  return {
    ...incoming,
    agencyUser: {
      ...incoming.agencyUser,
      agency: {
        ...incoming.agencyUser?.agency,
        subscription: {
          ...incoming.agencyUser?.agency.subscription,
          usageOverageInvoices: {
            ...incomingConnection,
            edges: [...previousConnection.edges, ...incomingConnection.edges]
          }
        }
      }
    }
  };
};
